import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type YoutubePlaybackStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'ended' | 'error';

export interface YoutubePlaybackMatch {
  id: string | number;
  youtubeVideoId: string;
  workId?: string;
  startMs?: number | null;
  endMs?: number | null;
  performerCredit?: string;
  title?: string;
}

export interface YoutubePlaybackError {
  code: string;
  message: string;
}

export interface YoutubePlaybackState {
  status: YoutubePlaybackStatus;
  currentMatchId: YoutubePlaybackMatch['id'] | null;
  currentMatch: YoutubePlaybackMatch | null;
  positionMs: number;
  durationMs: number | null;
  isMuted: boolean;
  autoplayBlocked: boolean;
  shouldAutoplay: boolean;
  error: YoutubePlaybackError | null;
}

const initialState: YoutubePlaybackState = {
  status: 'idle',
  currentMatchId: null,
  currentMatch: null,
  positionMs: 0,
  durationMs: null,
  isMuted: false,
  autoplayBlocked: false,
  shouldAutoplay: true,
  error: null,
};

const clampPosition = (positionMs: number): number => {
  if (!Number.isFinite(positionMs) || positionMs < 0) return 0;
  return positionMs;
};

const youtubePlaybackSlice = createSlice({
  name: 'youtubePlayback',
  initialState,
  reducers: {
    startYoutubePlayback(
      state,
      action: PayloadAction<{ match: YoutubePlaybackMatch; autoplay?: boolean; muted?: boolean }>
    ) {
      const { match, autoplay, muted } = action.payload;
      state.status = 'loading';
      state.currentMatch = match;
      state.currentMatchId = match.id ?? null;
      state.positionMs = clampPosition(match.startMs ?? 0);
      state.durationMs = null;
      state.shouldAutoplay = autoplay !== false;
      state.autoplayBlocked = false;
      state.error = null;
      if (typeof muted === 'boolean') {
        state.isMuted = muted;
      }
    },
    youtubePlaybackStarted(
      state,
      action: PayloadAction<{ positionMs?: number; durationMs?: number | null }>
    ) {
      state.status = 'playing';
      if (typeof action.payload.positionMs === 'number') {
        state.positionMs = clampPosition(action.payload.positionMs);
      }
      if (typeof action.payload.durationMs !== 'undefined') {
        state.durationMs = action.payload.durationMs ?? null;
      }
      state.autoplayBlocked = false;
    },
    youtubePlaybackPaused(state, action: PayloadAction<{ positionMs?: number } | undefined>) {
      state.status = 'paused';
      if (action?.payload && typeof action.payload.positionMs === 'number') {
        state.positionMs = clampPosition(action.payload.positionMs);
      }
    },
    youtubePlaybackEnded(state, action: PayloadAction<{ positionMs?: number } | undefined>) {
      state.status = 'ended';
      if (action?.payload && typeof action.payload.positionMs === 'number') {
        state.positionMs = clampPosition(action.payload.positionMs);
      }
    },
    stopYoutubePlayback(state) {
      state.status = 'idle';
      state.currentMatchId = null;
      state.currentMatch = null;
      state.positionMs = 0;
      state.durationMs = null;
      state.autoplayBlocked = false;
      state.error = null;
      state.shouldAutoplay = true;
    },
    setYoutubePlaybackPosition(state, action: PayloadAction<number>) {
      state.positionMs = clampPosition(action.payload);
    },
    setYoutubePlaybackDuration(state, action: PayloadAction<number | null | undefined>) {
      if (typeof action.payload === 'undefined') return;
      state.durationMs = action.payload ?? null;
    },
    setYoutubePlaybackMuted(state, action: PayloadAction<boolean>) {
      state.isMuted = action.payload;
    },
    setYoutubeAutoplayBlocked(state, action: PayloadAction<boolean>) {
      state.autoplayBlocked = action.payload;
      if (action.payload && state.status === 'loading') {
        state.status = 'paused';
      }
    },
    setYoutubePlaybackError(state, action: PayloadAction<YoutubePlaybackError>) {
      state.error = action.payload;
      state.status = 'error';
    },
    clearYoutubePlaybackError(state) {
      state.error = null;
    },
  },
});

export const youtubePlaybackReducer = youtubePlaybackSlice.reducer;
export const youtubePlaybackActions = youtubePlaybackSlice.actions;

type YoutubePlaybackRootState = { youtubePlayback: YoutubePlaybackState };

const selectYoutubePlaybackState = (state: YoutubePlaybackRootState) => state.youtubePlayback;

export const selectYoutubePlaybackStatus = (state: YoutubePlaybackRootState) =>
  selectYoutubePlaybackState(state).status;

export const selectYoutubePlaybackMatch = (state: YoutubePlaybackRootState) =>
  selectYoutubePlaybackState(state).currentMatch;

export const selectYoutubePlaybackMatchId = (state: YoutubePlaybackRootState) =>
  selectYoutubePlaybackState(state).currentMatchId;

export const selectYoutubePlaybackError = (state: YoutubePlaybackRootState) =>
  selectYoutubePlaybackState(state).error;

export const selectYoutubePlaybackPosition = (state: YoutubePlaybackRootState) =>
  selectYoutubePlaybackState(state).positionMs;

export const selectYoutubePlaybackDuration = (state: YoutubePlaybackRootState) =>
  selectYoutubePlaybackState(state).durationMs;

export const selectYoutubePlaybackMuted = (state: YoutubePlaybackRootState) =>
  selectYoutubePlaybackState(state).isMuted;

export const selectYoutubeAutoplayBlocked = (state: YoutubePlaybackRootState) =>
  selectYoutubePlaybackState(state).autoplayBlocked;

export const selectYoutubeShouldAutoplay = (state: YoutubePlaybackRootState) =>
  selectYoutubePlaybackState(state).shouldAutoplay;

export const selectYoutubeIsPlaying = createSelector(selectYoutubePlaybackStatus, (status) => status === 'playing');

export const selectYoutubeIsActive = createSelector(
  selectYoutubePlaybackState,
  (state) => state.currentMatchId !== null
);

export const selectYoutubeHasError = createSelector(
  selectYoutubePlaybackError,
  (error) => error !== null
);

export default youtubePlaybackReducer;
